// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo, useState } from "react";
import { css } from "styled-components";
import useIconIndex, { IconKey, iconOptions } from "@store/Framework/Common/Hooks/IconIndex/useIconIndex";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowiOSUpward from "@toolkit/Library/Components/Icons/ArrowiOSUpward";
import { useDebounce } from "@store/Framework/Common/Hooks/useDebounce";
import { getCssUnitFieldDefinition } from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";

const enum fields {
    size = "size",
    right = "right",
    bottom = "bottom",
    icon = "icon",
    threshold = "threshold",
    customCSS = "customCSS",
    hasText = "hasText",
    text = "text",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.size]: number;
        [fields.right]: string;
        [fields.bottom]: string;
        [fields.icon]: IconKey;
        [fields.threshold]: number;
        [fields.customCSS]: string;
        [fields.hasText]: boolean;
        [fields.text]: string;
    };
}

export interface GoToTopButtonStyles {
    buttonWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    textTypography?: TypographyPresentationProps;
}

export const goToTopButtonStyles: GoToTopButtonStyles = {
    textTypography: {},
    buttonWrapper: {
        css: css`
            position: fixed;
            display: flex;
            z-index: 50;
            box-shadow: ${({ theme }) => theme.shadows["main"]};
            bottom: 3%;
            right: 3%;
            flex-direction: column;
            opacity: 0;
            transition: opacity 0.4s ease-in-out;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            background: ${getColor("common.background")};
            border-radius: 50%;
            width: ${pxToRem(50)};
            height: ${pxToRem(50)};
            color: ${getColor("common.backgroundContrast")};
            :hover {
                outline: 2px solid ${getColor("primary")};
                color: ${getColor("primary")};
            }
            svg {
                color: ${getColor("common.backgroundContrast")};
            }
            :hover {
                svg {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
};

const getButtonStyles = (
    hasText: boolean,
    isVisible: boolean,
    right: string,
    bottom: string,
    size: number,
    baseStyles: InjectableCss | undefined,
): InjectableCss => {
    const buttonRight = right;
    const buttonBottom = bottom;
    const buttonSize = hasText ? size * 2.25 : size * 1.25;
    return {
        css: css`
            ${baseStyles?.css};
            right: ${buttonRight};
            bottom: ${buttonBottom};
            opacity: ${isVisible ? 1 : 0};
            width: ${pxToRem(buttonSize)};
            height: ${pxToRem(buttonSize)};
        `,
    };
};

export const GoToTopButton: React.FC<OwnProps> = ({ fields }) => {
    const isEditing = useShellContext().isEditing;
    const { size, right, bottom, icon, threshold, customCSS, hasText, text } = fields;
    const [isVisible, setIsVisible] = useState(false);
    const IconComponent = useIconIndex(icon)?.iconComponent ?? ArrowiOSUpward;
    const handleScroll = () => {
        setIsVisible(window.scrollY > threshold);
    };
    const debouncedHandleScroll = useDebounce(handleScroll, 200);
    useEffect(() => {
        window.addEventListener("scroll", debouncedHandleScroll);
        return () => window.removeEventListener("scroll", debouncedHandleScroll);
    }, [threshold]);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const buttonStyles = useMemo(
        () => getButtonStyles(hasText, isEditing || isVisible, right, bottom, size, goToTopButtonStyles.buttonWrapper),
        [size, right, bottom, goToTopButtonStyles.buttonWrapper, isVisible, hasText],
    );
    const customCssWrapper: InjectableCss = {
        css: css`
            ${customCSS}
        `,
    };
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper
                {...buttonStyles}
                className={isVisible ? "goToTop-button-wrapper-visible" : "goToTop-button-wrapper-hidden"}
                onClick={scrollToTop}
                role="button"
                aria-label="Go to top of page"
            >
                <Icon src={IconComponent} {...goToTopButtonStyles.icon} size={size} className="goToTop-icon" />
                {hasText ? <Typography className="goToTop-button-text">{text}</Typography> : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.goToTop-button-wrapper-visible {
}
.goToTop-button-wrapper-hidden {
}
.goToTop-icon {
}
.goToTop-button-text {
`;

const bottomField = getCssUnitFieldDefinition(fields.bottom);
const rightField = getCssUnitFieldDefinition(fields.right);
const widgetModule: WidgetModule = {
    component: GoToTopButton,
    definition: {
        displayName: "Go To Top Button",
        group: "Basic",
        icon: "arrow-up",
        fieldDefinitions: [
            {
                name: fields.hasText,
                displayName: "Has Text",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.text,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Top",
                fieldType: "Translatable",
                isRequired: true,
                isVisible: ({ fields: { hasText } }) => hasText,
            },
            {
                name: fields.size,
                displayName: "Button Size",
                editorTemplate: "IntegerField",
                defaultValue: 32,
                fieldType: "General",
                isRequired: true,
            },

            {
                ...rightField,
                displayName: "Distance from Right",
                editorTemplate: "TextField",
                defaultValue: "3%",
                fieldType: "General",
                isRequired: true,
            },
            {
                ...bottomField,
                displayName: "Distance from Bottom",
                editorTemplate: "TextField",
                defaultValue: "3%",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.icon,
                displayName: "Button Icon",
                editorTemplate: "DropDownField",
                options: iconOptions,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.threshold,
                displayName: "Scroll Threshold (px)",
                editorTemplate: "IntegerField",
                defaultValue: 300,
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: {
                    displayName: "Advanced",
                    sortOrder: 1,
                },
                defaultValue: defaultCustomCss,
                isVisible: (_, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
